import { useEffect, useState, useRef } from 'react';
import { initI18n, useTranslation } from '../../shared/i18n/index';
import { PublicLayout } from '../../shared/components';

/**
 * AuthCallback page - handles OAuth redirect with JWT tokens
 *
 * OAuth Flow:
 * 1. User clicks OAuth provider button (Google, Facebook, etc.)
 * 2. Rails handles OAuth callback from provider
 * 3. Rails generates JWT tokens and redirects to:
 *    /auth/callback#access_token=xxx&refresh_token=yyy&user_type=user
 * 4. This component extracts tokens from URL fragment
 * 5. Stores tokens (triggers login state)
 * 6. Redirects user to appropriate dashboard
 *
 * Security: URL fragment (#) is never sent to server, only processed client-side
 */
const AuthCallback = ({ locale = 'vi', onAuthComplete }) => {
  // Initialize i18n only once on mount
  const i18nInitialized = useRef(false);
  if (!i18nInitialized.current) {
    initI18n(locale);
    i18nInitialized.current = true;
  }
  const { t } = useTranslation();

  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);
  const processedRef = useRef(false);

  useEffect(() => {
    // Prevent double processing in React StrictMode
    if (processedRef.current) return;
    processedRef.current = true;

    const processCallback = async () => {
      try {
        // Get the URL fragment (everything after #)
        const fragment = window.location.hash.substring(1);

        if (!fragment) {
          // No fragment - check for error in query params (some OAuth errors come this way)
          const searchParams = new URLSearchParams(window.location.search);
          const errorParam = searchParams.get('error');
          if (errorParam) {
            throw new Error(searchParams.get('error_description') || errorParam);
          }
          throw new Error('No authentication data received');
        }

        // Parse the fragment as URL search params
        const params = new URLSearchParams(fragment);
        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');
        const userType = params.get('user_type');
        const errorParam = params.get('error');

        // Check for error in fragment
        if (errorParam) {
          throw new Error(params.get('error_description') || errorParam);
        }

        // Validate required tokens
        if (!accessToken) {
          throw new Error('Missing access token');
        }

        // Clear the hash from URL for security (tokens shouldn't linger in browser history)
        if (window.history.replaceState) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }

        // Store tokens in memory via callback (parent component should handle this with AuthContext)
        if (onAuthComplete) {
          await onAuthComplete({
            accessToken,
            refreshToken,
            userType
          });
        }

        setStatus('success');

        // Redirect to appropriate dashboard after short delay
        setTimeout(() => {
          const redirectPath = userType === 'child' ? '/children' : '/guardian';
          window.location.href = redirectPath;
        }, 1000);

      } catch (err) {
        console.error('[AuthCallback] Error processing callback:', err);
        setError(err.message || 'Authentication failed');
        setStatus('error');
      }
    };

    processCallback();
  }, [onAuthComplete]);

  const handleRetry = () => {
    window.location.href = '/login';
  };

  return (
    <PublicLayout t={t} showAuthButtons={false}>
      <div className="container d-flex justify-content-center align-items-center py-5" style={{ minHeight: 'calc(100vh - 56px)' }}>
        <div className="card border-0 shadow-lg rounded-4" style={{ width: '400px', maxWidth: '90vw' }}>
          <div className="card-body p-5 text-center">
            {status === 'processing' && (
              <>
                <div className="spinner-border text-primary mb-4" role="status" style={{ width: '3rem', height: '3rem' }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
                <h2 className="fw-bold mb-3">Đang xử lý...</h2>
                <p className="text-muted mb-0">Vui lòng đợi trong khi chúng tôi hoàn tất đăng nhập</p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" className="text-success" viewBox="0 0 16 16">
                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                  </svg>
                </div>
                <h2 className="fw-bold mb-3 text-success">Đăng nhập thành công!</h2>
                <p className="text-muted mb-0">Đang chuyển hướng...</p>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" className="text-danger" viewBox="0 0 16 16">
                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z"/>
                  </svg>
                </div>
                <h2 className="fw-bold mb-3 text-danger">Đăng nhập thất bại</h2>
                <p className="text-muted mb-4">{error}</p>
                <button
                  onClick={handleRetry}
                  className="btn btn-primary px-4 py-2"
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none'
                  }}
                >
                  Thử lại
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default AuthCallback;
